/**
 * Browser wiring: caching and politeness, decided once.
 *
 * The libraries neither cache nor rate-limit; the app chooses both here and
 * passes them down, as in Trailflora.
 */

import { createCache, indexedDbStore, memoryStore } from "@viamissa/cache";
import { createRateLimiter, type RequestOptions } from "@viamissa/http";

const hasIndexedDb = typeof indexedDB !== "undefined";

/** Persistent across reloads; memory in private windows where IndexedDB is refused. */
export const cache = createCache(hasIndexedDb ? indexedDbStore("viamissa", "api-cache") : memoryStore(), "v1");

/** masstimes.org: at most two requests in flight, a quarter second apart. */
export const masstimesHttp: RequestOptions = {
  limiter: createRateLimiter({ concurrency: 2, minIntervalMs: 250 }),
  timeoutMs: 15_000,
  retries: 1,
};

/** The public Valhalla and OSRM servers ask for about one request a second. */
export const routingHttp: RequestOptions = {
  limiter: createRateLimiter({ concurrency: 1, minIntervalMs: 1_000 }),
  timeoutMs: 30_000,
  retries: 1,
};

export const geocodingHttp: RequestOptions = {
  limiter: createRateLimiter({ concurrency: 1, minIntervalMs: 500 }),
  timeoutMs: 10_000,
  retries: 1,
};

const HOUR = 60 * 60 * 1000;

export const TTL = {
  /** Schedules change rarely; a week keeps repeat trips fast without going stale. */
  churches: 7 * 24 * HOUR,
  route: 24 * HOUR,
  detour: 7 * 24 * HOUR,
  geocode: 30 * 24 * HOUR,
} as const;
